import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { useRedirect } from '../contexts/RedirectContext';
import { useDataCache } from '../contexts/DataCacheContext';
import { useProject } from '../contexts/ProjectContext';
import optimizedDataService from '../services/OptimizedDataService';

const CompanyList = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { projects: contextProjects, defaultProject, isValidProject, projectsLoaded } = useProject();
  const initialProject = (projectId && isValidProject(projectId)) ? projectId : defaultProject;
  const [activeProject, setActiveProject] = useState(initialProject);
  const hasUserSelected = useRef(false);
  const hasSyncedDefault = useRef(false);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [refreshing, setRefreshing] = useState(false);
  const { isRedirectEnabled, setRedirectEnabled, getRedirectLinks, setRedirectLinksFromData } = useRedirect();
  const dataCache = useDataCache();

  const projects = contextProjects;

  // Sync local activeProject with Firestore default once loaded (only if user hasn't manually picked)
  useEffect(() => {
    if (projectsLoaded && !hasSyncedDefault.current && !projectId && !hasUserSelected.current) {
      setActiveProject(defaultProject);
      hasSyncedDefault.current = true;
    }
  }, [projectsLoaded, defaultProject, projectId]);

  const handleProjectChange = useCallback((e) => {
    hasUserSelected.current = true;
    setActiveProject(e.target.value);
  }, []);

  // Initialize optimized data service with cache context
  useEffect(() => {
    optimizedDataService.setCache(dataCache);
  }, [dataCache]);

  useEffect(() => {
    let isMounted = true;
    
    // Load data using optimized service
    const loadCompanies = async () => {
      try {
        setLoading(true);
        console.log(`🔄 CompanyList: Starting to load companies for project: ${activeProject}`);
        console.log(`🔄 CompanyList: DataService cache context:`, dataCache);
        
        // Use optimized service only
        const companies = await optimizedDataService.getAllCompanies(activeProject);
        
        // Check if component is still mounted before updating state
        if (!isMounted) {
          console.log('⏹️ CompanyList: Component unmounted, skipping state update');
          return;
        }
        
        console.log(`✅ Used optimized data service for ${activeProject}`);
        console.log(`✅ CompanyList: Successfully loaded ${companies.length} companies for ${activeProject}`);
        console.log(`🏢 CompanyList: First company sample:`, companies[0]);
        setCompanies(companies);
        setFilteredCompanies(companies);
        
        setLoading(false);
      } catch (error) {
        console.error('❌ CompanyList: Error loading companies:', error);
        
        // Only update state if component is still mounted
        if (isMounted) {
          setCompanies([]);
          setFilteredCompanies([]);
          setLoading(false);
        }
      }
    };

    // Load companies immediately
    loadCompanies();
    
    return () => {
      isMounted = false;
    };
  }, [activeProject]);

  useEffect(() => {
    // Filter companies based on search term
    const filtered = companies.filter(company => {
      const searchLower = searchTerm.toLowerCase();
      return (
        (company.exhibitor_en && company.exhibitor_en.toLowerCase().includes(searchLower)) ||
        (company.exhibitor_tc && company.exhibitor_tc.toLowerCase().includes(searchLower)) ||
        (company.exhibitor_sc && company.exhibitor_sc.toLowerCase().includes(searchLower)) ||
        (company.contact_person_name_en && company.contact_person_name_en.toLowerCase().includes(searchLower)) ||
        (company.contact_person_name_tc && company.contact_person_name_tc.toLowerCase().includes(searchLower)) ||
        (company.contact_person_name_sc && company.contact_person_name_sc.toLowerCase().includes(searchLower)) ||
        (company.code && company.code.toLowerCase().includes(searchLower))
      );
    });
    setFilteredCompanies(filtered);
  }, [searchTerm, companies]);

  const redirectEnabled = isRedirectEnabled(activeProject);
  const redirectLinks = getRedirectLinks(activeProject);

  // Load redirect links from company data when companies are loaded
  useEffect(() => {
    if (companies.length > 0) {
      setRedirectLinksFromData(activeProject, companies);
    }
  }, [companies, activeProject, setRedirectLinksFromData]);

  // Sorting function
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Sort the filtered companies
  const getSortedCompanies = () => {
    if (!sortConfig.key) return filteredCompanies;

    return [...filteredCompanies].sort((a, b) => {
      let aValue = a[sortConfig.key] || '';
      let bValue = b[sortConfig.key] || '';

      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      // Clear cache and reload data
      dataCache.clearCache(activeProject, 'companies');
      console.log(`🗑️ Cleared cache for ${activeProject} companies`);
      
      const companies = await optimizedDataService.getAllCompanies(activeProject);
      console.log(`✅ Refreshed: loaded ${companies.length} companies for ${activeProject}`);
      setCompanies(companies);
      setFilteredCompanies(companies);
    } catch (error) {
      console.error('Error refreshing companies:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const parseCSV = (csvText) => {
    const lines = csvText.split('\n');
    const headers = lines[0].split(',');
    const companies = [];

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (line) {
        const values = parseCSVLine(line);
        if (values.length >= headers.length) {
          const company = {};
          headers.forEach((header, index) => {
            company[header.trim()] = values[index] ? values[index].trim() : '';
          });
          // Only add companies that have a name
          if (company.exhibitor_en || company.exhibitor_tc || company.exhibitor_sc) {
            companies.push(company);
          }
        }
      }
    }
    return companies;
  };

  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current);
    return result;
  };

  const handleCompanyClick = (company) => {
    if (company.code) {
      navigate(`/${activeProject}/company/${company.code}`);
    }
  };

  const handleWebsiteClick = (website, e) => {
    e.stopPropagation();
    if (website && website.startsWith('http')) {
      window.open(website, '_blank');
    } else if (website && !website.startsWith('http')) {
      window.open(`https://${website}`, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-900">
        <div className="text-lg text-white">Loading companies...</div>
      </div>
    );
  }

  const getProjectPath = (path) => {
    return `/${activeProject}${path}`;
  };

  return (
    <>
      <nav className="bg-gray-900 shadow-lg border-b border-gray-700">
        <div className="container mx-auto px-4 py-3">
          {/* Mobile Navigation */}
          <div className="md:hidden">
            {/* Logo Row - Centered */}
            <div className="flex justify-center mb-3">
              <img src="/virtrix-logo.png" alt="VIRTRIX Logo" className="h-8 w-auto" />
            </div>
            
            {/* Title and Navigation Row */}
            <div className="flex items-center justify-between">
              <h1 className="text-lg font-bold text-white">HK Pavilion CMS</h1>
              <div className="flex space-x-1">
                <a href={getProjectPath("/books/booklist")} className="text-gray-300 hover:text-white text-xs px-2 py-1 rounded border border-gray-600">Book List</a>
                <a href={getProjectPath("/companies")} className="text-white font-semibold bg-blue-600 px-2 py-1 rounded text-xs">Companies</a>
                <a href={getProjectPath("/zones")} className="text-gray-300 hover:text-white text-xs px-2 py-1 rounded border border-gray-600">Zone QR</a>
                <a href="/admin" className="text-gray-300 hover:text-white text-xs px-2 py-1 rounded border border-gray-600">Admin</a>
              </div>
            </div>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden md:block">
            {/* Logo Row - Centered */}
            <div className="flex justify-center mb-4">
              <img src="/virtrix-logo.png" alt="VIRTRIX Logo" className="h-10 w-auto" />
            </div>
            
            {/* Title and Controls Row */}
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-4">
                <h1 className="text-2xl font-bold text-white">HK Pavilion CMS</h1>
                <div className="relative">
                  <select
                    value={activeProject}
                    onChange={handleProjectChange}
                    className="bg-gray-800 text-white border border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {projects.map(project => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  onClick={handleRefresh}
                  disabled={refreshing}
                  className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                    refreshing
                      ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700 text-white'
                  }`}
                >
                  {refreshing ? 'Refreshing...' : '🔄 Refresh'}
                </button>
              </div>
              <div className="space-x-4">
                <a href={getProjectPath("/books/booklist")} className="text-gray-300 hover:text-white">Book List</a>
                <a href={getProjectPath("/companies")} className="text-white font-semibold bg-blue-600 px-3 py-1 rounded">Companies</a>
                <a href={getProjectPath("/zones")} className="text-gray-300 hover:text-white">Zone QR</a>
                <a href="/admin" className="text-gray-300 hover:text-white">Admin</a>
              </div>
            </div>
          </div>
        </div>
      </nav>
      <div className="min-h-screen bg-gray-900">
        <div className="w-full px-4 py-2 md:py-4">
          <div className="mb-4 bg-transparent">
            {/* Mobile Controls and Search Row */}
            <div className="md:hidden flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                <div className="relative">
                  <select
                    value={activeProject}
                    onChange={handleProjectChange}
                    className="bg-gray-800 text-white border border-gray-600 rounded px-2 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 outline outline-2 outline-blue-600"
                  >
                    {projects.map(project => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  onClick={handleRefresh}
                  disabled={refreshing}
                  className={`px-2 pb-2 text-lg transition-colors flex items-center justify-center ${
                    refreshing
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-blue-400 hover:text-blue-300'
                  }`}
                >
                  {refreshing ? '⏳' : '🔄'}
                </button>
              </div>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search companies..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-48 px-3 py-2 bg-gray-900 text-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-400 text-sm outline outline-2 outline-blue-600"
                />
              </div>
            </div>
            
            {/* Desktop Title and Search */}
            <div className="hidden md:block">
              <h1 className="text-xl md:text-2xl font-bold mb-2 md:mb-3 text-white">
                {projects.find(p => p.id === activeProject)?.name} - Companies
              </h1>
            </div>
            
            {/* Desktop Layout - Controls on the right */}
            <div className="hidden md:flex justify-between items-center">
              <p className="text-gray-200 text-base font-medium">
                Showing {filteredCompanies.length} of {companies.length} companies
              </p>
              
              <div className="flex items-center space-x-4">
                {/* Desktop Search Bar */}
                <div className="relative w-96">
                  <input
                    type="text"
                    placeholder="Search companies..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-4 py-2 bg-gray-800 text-white border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-400 text-sm outline outline-2 outline-blue-600"
                  />
                </div>
                
                {/* Redirect Toggle - Desktop */}
                <div className="flex items-center justify-between bg-gray-700 px-4 py-1.5 rounded-lg border border-gray-500 w-56">
                  <span className="text-white text-sm font-medium">Redirect QR Codes:</span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={redirectEnabled}
                      onChange={(e) => setRedirectEnabled(activeProject, e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-400 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                  </label>
                </div>
              </div>
            </div>
            
            {/* Mobile Layout - Controls on the right */}
            <div className="md:hidden flex justify-between items-center">
              <p className="text-gray-200 text-sm font-medium">
                Showing {filteredCompanies.length} of {companies.length} companies
              </p>
              
              <div className="flex items-center space-x-2">
                {/* Redirect Toggle - Mobile optimized (30% smaller) */}
                <div className="flex items-center justify-between p-2">
                  <span className="text-white text-xs font-medium mr-3">Redirect QR:</span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={redirectEnabled}
                      onChange={(e) => setRedirectEnabled(activeProject, e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-8 h-4 bg-gray-400 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-green-500"></div>
                  </label>
                </div>
              </div>
            </div>
            {companies.length === 0 && (
              <p className="text-yellow-400 mt-2">
                No companies uploaded yet for {projects.find(p => p.id === activeProject)?.name || activeProject.toUpperCase()}. Use the Admin panel to upload company data.
              </p>
            )}
          </div>

          <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 overflow-hidden mt-1 md:mt-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm md:text-base">
                <thead className="bg-gray-700">
                  <tr>
                    <th 
                      className="px-1 md:px-3 py-2 md:py-4 text-left text-xs font-semibold text-gray-200 uppercase tracking-wider cursor-pointer hover:bg-gray-600 transition-colors"
                      style={{width: '80px', minWidth: '80px'}}
                      onClick={() => handleSort('code')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Code</span>
                        {sortConfig.key === 'code' && (
                          <span className="text-blue-400">
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-1 md:px-3 py-2 md:py-4 text-left text-xs font-semibold text-gray-200 uppercase tracking-wider cursor-pointer hover:bg-gray-600 transition-colors"
                      style={{width: '120px', minWidth: '120px'}}
                      onClick={() => handleSort('exhibitor_en')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Company (EN)</span>
                        {sortConfig.key === 'exhibitor_en' && (
                          <span className="text-blue-400">
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-1 md:px-3 py-2 md:py-4 text-left text-xs font-semibold text-gray-200 uppercase tracking-wider cursor-pointer hover:bg-gray-600 transition-colors"
                      style={{width: '120px', minWidth: '120px'}}
                      onClick={() => handleSort('exhibitor_tc')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Company (TC)</span>
                        {sortConfig.key === 'exhibitor_tc' && (
                          <span className="text-blue-400">
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-1 md:px-3 py-2 md:py-4 text-left text-xs font-semibold text-gray-200 uppercase tracking-wider cursor-pointer hover:bg-gray-600 transition-colors"
                      style={{width: '100px', minWidth: '100px'}}
                      onClick={() => handleSort('contact_person_name_en')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Contact</span>
                        {sortConfig.key === 'contact_person_name_en' && (
                          <span className="text-blue-400">
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-1 md:px-3 py-2 md:py-4 text-left text-xs font-semibold text-gray-200 uppercase tracking-wider cursor-pointer hover:bg-gray-600 transition-colors"
                      style={{width: '120px', minWidth: '120px'}}
                      onClick={() => handleSort('contact_person_email')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Email</span>
                        {sortConfig.key === 'contact_person_email' && (
                          <span className="text-blue-400">
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-1 md:px-3 py-2 md:py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider"
                      style={{width: '100px', minWidth: '100px'}}
                    >
                      Redirect
                    </th>
                    <th 
                      className="px-1 md:px-3 py-2 md:py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider"
                      style={{width: '120px', minWidth: '120px'}}
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-gray-800 divide-y divide-gray-700">
                  {getSortedCompanies().map((company, index) => (
                    <tr key={index} className="hover:bg-gray-700 transition-colors duration-200">
                      <td className="px-1 md:px-3 py-2 md:py-4" style={{width: '80px', minWidth: '80px'}}>
                        <div className="text-xs md:text-sm font-semibold text-white">
                          {company.code || '-'}
                        </div>
                      </td>
                      <td className="px-1 md:px-3 py-2 md:py-4" style={{width: '120px', minWidth: '120px'}}>
                        <div className="text-xs md:text-sm text-gray-100" title={company.exhibitor_en || '-'}>
                          {company.exhibitor_en || '-'}
                        </div>
                      </td>
                      <td className="px-1 md:px-3 py-2 md:py-4" style={{width: '120px', minWidth: '120px'}}>
                        <div className="text-xs md:text-sm text-gray-100" title={company.exhibitor_tc || '-'}>
                          {company.exhibitor_tc || '-'}
                        </div>
                      </td>
                      <td className="px-1 md:px-3 py-2 md:py-4" style={{width: '100px', minWidth: '100px'}}>
                        <div className="text-xs md:text-sm text-gray-200" title={company.contact_person_name_en || company.contact_person_name_tc || company.contact_person_name_sc || '-'}>
                          {company.contact_person_name_en || company.contact_person_name_tc || company.contact_person_name_sc || '-'}
                        </div>
                      </td>
                      <td className="px-1 md:px-3 py-2 md:py-4" style={{width: '120px', minWidth: '120px'}}>
                        <div className="text-xs md:text-sm text-gray-200" title={company.contact_person_email || '-'}>
                          {company.contact_person_email || '-'}
                        </div>
                      </td>
                      <td className="px-1 md:px-3 py-2 md:py-4" style={{width: '100px', minWidth: '100px'}}>
                        <div className="text-xs md:text-sm text-gray-200" title={redirectLinks[company.code] || '-'}>
                          {redirectLinks[company.code] ? (
                            <>
                              {/* Mobile: Show "Link" text */}
                              <span className="md:hidden text-blue-400 hover:text-blue-300 underline transition-colors text-xs">
                                <a
                                  href={redirectLinks[company.code]}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  Link
                                </a>
                              </span>
                              {/* Desktop: Show full URL */}
                              <a
                                href={redirectLinks[company.code]}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="hidden md:inline text-blue-400 hover:text-blue-300 underline transition-colors text-xs break-all"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {redirectLinks[company.code]}
                              </a>
                            </>
                          ) : (
                            '-'
                          )}
                        </div>
                      </td>
                      <td className="px-1 md:px-3 py-2 md:py-4 text-xs md:text-sm font-medium" style={{width: '120px', minWidth: '120px'}}>
                        <div className="flex space-x-2">
                          {company.code && (
                            <button
                              onClick={() => handleCompanyClick(company)}
                              className="text-xs md:text-sm transition-colors text-blue-400 hover:text-blue-300 font-medium"
                            >
                              View Details
                            </button>
                          )}
                          {company.company_website && (
                            <button
                              onClick={(e) => handleWebsiteClick(company.company_website, e)}
                              className="text-green-400 hover:text-green-300 transition-colors text-xs md:text-sm font-medium"
                            >
                              Website
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {filteredCompanies.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-300 text-lg font-medium">
                {companies.length === 0
                  ? `No companies uploaded yet for ${projects.find(p => p.id === activeProject)?.name || activeProject.toUpperCase()}. Use the Admin panel to upload company data.`
                  : 'No companies found matching your search.'
                }
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CompanyList;
